import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { LoadingButton } from '@/components/ui/loading-button';
import { GoogleLogo } from '@/components/ui/google-logo';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/store/authStore';
import { useGoogleOAuth } from '@/hooks/use-google-oauth';
import { useAppleOAuth } from '@/hooks/use-apple-oauth';
import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/api-error';

/**
 * Login rápido em dialog para o cliente nas páginas públicas do prestador —
 * evita sair da página para a tela de login. Email/senha + social. Sucesso
 * fecha o dialog (a sessão passa a aparecer no header). O `login` do store
 * cuida da navegação pós-auth, como na tela cheia. "Esqueci a senha" troca o
 * formulário pelo pedido do link de recuperação, sem sair da página.
 */
export function LoginDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const login = useAuthStore((s) => s.login);
  const { signInWithGoogle, isGoogleLoading } = useGoogleOAuth();
  const { signInWithApple, isAppleLoading } = useAppleOAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [forgotSent, setForgotSent] = useState(false);

  const social = isGoogleLoading || isAppleLoading;
  const busy = loading || social;

  const submit = async () => {
    if (!email.trim() || password.length < 6) {
      setError('Informe e-mail e senha (mínimo de 6 caracteres).');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await login({ email: email.trim(), password });
      onClose();
    } catch {
      setError('E-mail ou senha incorretos.');
    } finally {
      setLoading(false);
    }
  };

  const sendForgot = async () => {
    if (!email.trim()) {
      setError('Informe o e-mail da sua conta.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // A API responde igual exista ou não a conta (anti-enumeração).
      await api.post('/auth/forgot-password', { email: email.trim() });
      setForgotSent(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o e-mail. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: 'login' | 'forgot') => {
    setMode(next);
    setError(null);
    setForgotSent(false);
  };

  const close = () => {
    switchMode('login');
    onClose();
  };

  const goTo = (path: string) => {
    onClose();
    router.push(path as never);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent>
        {mode === 'forgot' ? (
          <>
            <DialogHeader>
              <DialogTitle>Esqueci a senha</DialogTitle>
              <DialogDescription>
                {forgotSent
                  ? 'Se houver uma conta com esse e-mail, enviamos o link para criar uma senha nova. Confira também a caixa de spam.'
                  : 'Informe o e-mail da sua conta e enviamos um link para criar uma senha nova.'}
              </DialogDescription>
            </DialogHeader>
            <View className="mt-4 gap-3">
              {!forgotSent && (
                <Input
                  placeholder="seu@email.com"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  editable={!loading}
                />
              )}
              {!!error && (
                <View className="flex-row items-center gap-2 rounded-xl bg-red-50 px-3 py-2.5">
                  <Ionicons className="text-error-600" name="alert-circle" size={17} />
                  <ThemedText className="flex-1 text-sm text-red-700">{error}</ThemedText>
                </View>
              )}
              {!forgotSent && (
                <LoadingButton
                  title="Enviar link"
                  onPress={sendForgot}
                  loading={loading}
                  disabled={loading}
                  className="w-full"
                />
              )}
              <Pressable onPress={() => switchMode('login')} className="self-center py-1">
                <ThemedText className="font-semibold text-sm text-primary">
                  Voltar para entrar
                </ThemedText>
              </Pressable>
            </View>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Entrar</DialogTitle>
              <DialogDescription>
                Acesse sua conta para acompanhar seus agendamentos.
              </DialogDescription>
            </DialogHeader>

            <View className="mt-4 gap-3">
              <Input
                placeholder="seu@email.com"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                editable={!busy}
              />
              <Input
                placeholder="Senha"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                editable={!busy}
              />

              {!!error && (
                <View className="flex-row items-center gap-2 rounded-xl bg-red-50 px-3 py-2.5">
                  <Ionicons className="text-error-600" name="alert-circle" size={17} />
                  <ThemedText className="flex-1 text-sm text-red-700">{error}</ThemedText>
                </View>
              )}

              <Pressable onPress={() => switchMode('forgot')} className="self-end py-1">
                <ThemedText className="font-semibold text-sm text-primary">
                  Esqueci a senha
                </ThemedText>
              </Pressable>

              <LoadingButton
                title="Entrar"
                onPress={submit}
                loading={loading}
                disabled={busy}
                className="w-full"
              />

              <View className="flex-row items-center">
                <View className="h-px flex-1 bg-neutral-200" />
                <ThemedText className="mx-3 font-medium text-xs text-neutral-400">ou</ThemedText>
                <View className="h-px flex-1 bg-neutral-200" />
              </View>

              <Pressable
                onPress={signInWithGoogle}
                disabled={busy}
                className="h-12 w-full flex-row items-center justify-center gap-3 rounded-full border border-neutral-200 bg-card active:opacity-70"
              >
                <GoogleLogo size={18} />
                <ThemedText className="font-semibold text-sm text-neutral-800">
                  Entrar com Google
                </ThemedText>
              </Pressable>

              {Platform.OS === 'ios' && (
                <Pressable
                  onPress={signInWithApple}
                  disabled={busy}
                  className="h-12 w-full flex-row items-center justify-center gap-3 rounded-full border border-neutral-200 bg-card active:opacity-70"
                >
                  <Ionicons className="text-neutral-900" name="logo-apple" size={18} />
                  <ThemedText className="font-semibold text-sm text-neutral-800">
                    Entrar com Apple
                  </ThemedText>
                </Pressable>
              )}

              <View className="flex-row items-center justify-center pt-1">
                <ThemedText className="text-sm text-neutral-500">Não tem uma conta? </ThemedText>
                <Pressable onPress={() => goTo('/register')}>
                  <ThemedText className="font-bold text-sm text-primary">Cadastre-se</ThemedText>
                </Pressable>
              </View>
            </View>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
